import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { businessInfo } from '../data/mock';

export const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | Precision Mobile Detailing';
  }, []);

  return (
    <div className="min-h-screen bg-white py-20">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">
            <strong>Effective Date:</strong> January 1, 2026<br />
            <strong>Last Updated:</strong> January 1, 2026
          </p>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-3xl font-bold mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Precision Mobile Detailing ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
                <a href="https://precisiondetailingie.com" className="text-black font-semibold hover:underline"> precisiondetailingie.com</a> (the "Site") 
                or use our mobile car detailing services.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                By using our Site or services, you agree to the collection and use of information in accordance with this Privacy Policy. 
                If you do not agree with our policies and practices, please do not use our Site or services.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-3xl font-bold mb-4">2. Information We Collect</h2>
              
              <h3 className="text-2xl font-bold mb-3 mt-6">2.1 Personal Information</h3>
              <p className="text-gray-700 leading-relaxed">
                We may collect personal information that you voluntarily provide when you:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Request an appointment or service quote</li>
                <li>Contact us via email or phone</li>
                <li>Subscribe to our newsletter or marketing communications</li>
                <li>Fill out forms on our Site</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                This information may include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Service location/address</li>
                <li>Vehicle information</li>
                <li>Preferred appointment date and time</li>
                <li>Any additional details you provide in notes or messages</li>
              </ul>

              <h3 className="text-2xl font-bold mb-3 mt-6">2.2 Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed">
                When you visit our Site, we may automatically collect certain information about your device and usage, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Date and time of your visit</li>
              </ul>

              <h3 className="text-2xl font-bold mb-3 mt-6">2.3 Cookies and Tracking Technologies</h3>
              <p className="text-gray-700 leading-relaxed">
                We use cookies, web beacons, and similar tracking technologies to enhance your experience on our Site. 
                Cookies are small data files stored on your device. You can control cookies through your browser settings, 
                but disabling them may limit your ability to use certain features of our Site.
              </p>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-3xl font-bold mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>To Provide Services:</strong> Schedule appointments, confirm bookings, and deliver mobile detailing services</li>
                <li><strong>Communication:</strong> Send appointment confirmations, service reminders, and respond to inquiries</li>
                <li><strong>Marketing:</strong> Send promotional emails, special offers, and updates (with your consent)</li>
                <li><strong>Website Improvement:</strong> Analyze usage patterns to improve our Site and user experience</li>
                <li><strong>Legal Compliance:</strong> Comply with applicable laws and regulations</li>
                <li><strong>Fraud Prevention:</strong> Detect and prevent fraudulent transactions and protect our business</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section>
              <h2 className="text-3xl font-bold mb-4">4. How We Share Your Information</h2>
              <p className="text-gray-700 leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
              </p>
              
              <h3 className="text-2xl font-bold mb-3 mt-6">4.1 Service Providers</h3>
              <p className="text-gray-700 leading-relaxed">
                We may share your information with trusted third-party service providers who assist us in operating our Site and business, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Email Services:</strong> SendGrid for appointment confirmation emails</li>
                <li><strong>SMS Services:</strong> Twilio for text message notifications</li>
                <li><strong>Payment Processors:</strong> For processing payments (if applicable)</li>
                <li><strong>Analytics Providers:</strong> Google Analytics or similar services to analyze Site usage</li>
                <li><strong>Advertising Partners:</strong> Meta (Facebook/Instagram) for targeted advertising</li>
              </ul>

              <h3 className="text-2xl font-bold mb-3 mt-6">4.2 Legal Requirements</h3>
              <p className="text-gray-700 leading-relaxed">
                We may disclose your information if required to do so by law or in response to valid requests by public authorities 
                (e.g., a court or government agency).
              </p>

              <h3 className="text-2xl font-bold mb-3 mt-6">4.3 Business Transfers</h3>
              <p className="text-gray-700 leading-relaxed">
                If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-3xl font-bold mb-4">5. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Site may use third-party services that collect information used to identify you:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Google Maps:</strong> For displaying service area locations</li>
                <li><strong>Meta Pixel:</strong> For Facebook and Instagram advertising and analytics</li>
                <li><strong>Social Media Platforms:</strong> Links to our social media profiles</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                These third-party services have their own privacy policies. We recommend reviewing their policies as we are not responsible 
                for their privacy practices.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-3xl font-bold mb-4">6. Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal information from 
                unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the internet 
                or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-3xl font-bold mb-4">7. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, 
                unless a longer retention period is required or permitted by law. Appointment and service records may be retained for 
                business and tax purposes.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-3xl font-bold mb-4">8. Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails or SMS messages</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a structured, commonly used format</li>
                <li><strong>Objection:</strong> Object to processing of your personal information for certain purposes</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </section>

            {/* California Privacy Rights */}
            <section>
              <h2 className="text-3xl font-bold mb-4">9. California Privacy Rights (CCPA)</h2>
              <p className="text-gray-700 leading-relaxed">
                If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Right to know what personal information is collected</li>
                <li>Right to know whether personal information is sold or disclosed</li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to deletion of personal information</li>
                <li>Right to non-discrimination for exercising your rights</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>We do not sell your personal information.</strong>
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-3xl font-bold mb-4">10. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information 
                from children. If you believe we have collected information from a child, please contact us immediately so we can delete it.
              </p>
            </section>

            {/* Do Not Track */}
            <section>
              <h2 className="text-3xl font-bold mb-4">11. Do Not Track Signals</h2>
              <p className="text-gray-700 leading-relaxed">
                Some web browsers have a "Do Not Track" feature. Currently, we do not respond to Do Not Track signals, 
                as there is no industry standard for how such signals should be interpreted.
              </p>
            </section>

            {/* International Users */}
            <section>
              <h2 className="text-3xl font-bold mb-4">12. International Users</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are provided from the United States and are intended for users in California and the surrounding Inland Empire region. 
                If you access our Site from outside the United States, your information may be transferred to, stored, and processed in the United States.
              </p>
            </section>

            {/* Changes to Privacy Policy */}
            <section>
              <h2 className="text-3xl font-bold mb-4">13. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
                We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
                Your continued use of our Site after any changes indicates your acceptance of the updated Privacy Policy.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-3xl font-bold mb-4">14. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                <p className="font-bold text-xl mb-4">Precision Mobile Detailing</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-600" />
                    <a href={`tel:${businessInfo.phone}`} className="text-black font-semibold hover:underline">
                      {businessInfo.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-600" />
                    <a href={`mailto:${businessInfo.email}`} className="text-black font-semibold hover:underline">
                      {businessInfo.email}
                    </a>
                  </div>
                  <p className="text-gray-600">
                    Service Area: Redlands, Loma Linda, Yucaipa, and surrounding Inland Empire cities
                  </p>
                </div>
              </div>
            </section>

            {/* Consent */}
            <section className="mt-12 p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
              <h2 className="text-2xl font-bold mb-3">Your Consent</h2>
              <p className="text-gray-700 leading-relaxed">
                By using our Site and services, you consent to our Privacy Policy and agree to its terms. 
                If you do not agree with this Privacy Policy, please discontinue use of our Site and services.
              </p>
            </section>
          </div>

          {/* Back to Home */}
          <div className="mt-12 text-center">
            <Link 
              to="/" 
              className="inline-block bg-black text-white px-8 py-3 rounded hover:bg-gray-800 transition-colors font-semibold"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
